import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import AssignmentIcon from '@mui/icons-material/Assignment';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useEvents } from '../../contexts/EventContext';
import { formatDate, formatTime } from '../../utils/helpers';
import { getCategoryLabel, getCategoryColor } from '../../types';
import api from '../../services/api';
import { csvEsc, attendeeName, photoPermLabel, dietaryLabel } from '../../utils/attendeeExport';

interface RegisterAttendee {
  id: string;
  checkedIn: boolean;
  familyMember?: {
    name: string;
    relationship?: string;
    photoPermission?: boolean;
    dietaryRequirements?: string | null;
  } | null;
  eventGuest?: {
    firstName: string;
    lastName: string;
    relationship?: string;
    dietaryRequirements?: string | null;
  } | null;
}

interface EventRegistration {
  id: string;
  eventId: string;
  userId: string;
  attendeeCount: number;
  specialRequirements: string | null;
  status: string;
  registeredAt: string;
  cancelledAt: string | null;
  checkedIn?: boolean;
  checkedInAt?: string | null;
  attendees?: RegisterAttendee[];
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string | null;
  };
}

const EventRegisterPage: React.FC = () => {
  const { events } = useEvents();
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [loading, setLoading] = useState(false);

  const selectedEvent = events.find((e) => e.id === selectedEventId);

  // Fetch registrations from API when event is selected
  const fetchRegistrations = useCallback(async (eventId: string) => {
    setLoading(true);
    try {
      const res = await api.get(`/registrations/event/${eventId}`);
      setRegistrations(res.data);
    } catch {
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      fetchRegistrations(selectedEventId);
    } else {
      setRegistrations([]);
    }
  }, [selectedEventId, fetchRegistrations]);

  // Export to CSV
  const handleExportCSV = () => {
    if (!selectedEvent || registrations.length === 0) return;

    // C7 — one row per attendee, with Photo Permission + Dietary.
    const headers = ['Attendee Name', 'Relationship', 'Photo Permission', 'Dietary', 'Family (account)', 'Email', 'Phone', 'Status', 'Registered Date'];
    const rows = registrations.flatMap((reg) => {
      const family = `${reg.user?.firstName || ''} ${reg.user?.lastName || ''}`.trim();
      const email = reg.user?.email || 'N/A';
      const phone = reg.user?.phone || 'N/A';
      const registeredAt = new Date(reg.registeredAt).toLocaleDateString();
      const list = reg.attendees ?? [];
      if (list.length === 0) {
        return [[
          csvEsc(`${reg.attendeeCount} attendee(s)`), csvEsc(''), csvEsc('N/A'), csvEsc(''),
          csvEsc(family), csvEsc(email), csvEsc(phone), csvEsc(reg.status), csvEsc(registeredAt),
        ]];
      }
      return list.map((a) => [
        csvEsc(attendeeName(a)),
        csvEsc(a.familyMember?.relationship || a.eventGuest?.relationship || ''),
        csvEsc(photoPermLabel(a)),
        csvEsc(dietaryLabel(a)),
        csvEsc(family),
        csvEsc(email),
        csvEsc(phone),
        csvEsc(reg.status),
        csvEsc(registeredAt),
      ]);
    });

    const csvContent = [
      `Event: ${selectedEvent.title}`,
      `Date: ${formatDate(selectedEvent.date)}`,
      `Location: ${selectedEvent.location}`,
      '',
      headers.map(csvEsc).join(','),
      ...rows.map((row) => row.join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${selectedEvent.title.replace(/\s+/g, '_')}_Register.csv`;
    link.click();
  };

  // Print register
  const handlePrint = () => {
    window.print();
  };

  // Calculate totals
  const totalAttendees = registrations
    .filter((r) => r.status === 'confirmed')
    .reduce((sum, r) => sum + r.attendeeCount, 0);
  const confirmedCount = registrations.filter((r) => r.status === 'confirmed').length;
  const cancelledCount = registrations.filter((r) => r.status === 'cancelled').length;

  return (
    <Box>
      <Typography variant="h4" fontWeight={600} sx={{ mb: 1 }}>
        Event Register
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        View and export attendee registers for each event
      </Typography>

      {/* Event Selection */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <FormControl fullWidth>
            <InputLabel>Select Event</InputLabel>
            <Select
              value={selectedEventId}
              label="Select Event"
              onChange={(e) => setSelectedEventId(e.target.value)}
            >
              <MenuItem value="">
                <em>Choose an event...</em>
              </MenuItem>
              {events.map((event) => (
                <MenuItem key={event.id} value={event.id}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EventIcon sx={{ color: getCategoryColor(event.category), fontSize: 20 }} />
                    <span>{event.title}</span>
                    <Chip
                      label={formatDate(event.date)}
                      size="small"
                      sx={{ ml: 1, fontSize: '0.7rem' }}
                    />
                    <Chip
                      label={getCategoryLabel(event.category)}
                      size="small"
                      sx={{
                        backgroundColor: getCategoryColor(event.category),
                        color: 'white',
                        fontSize: '0.65rem',
                      }}
                    />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </CardContent>
      </Card>

      {!selectedEventId ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <AssignmentIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            Select an event to view its register
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose an event from the dropdown above to see the list of registered attendees.
          </Typography>
        </Paper>
      ) : (
        <>
          {/* Event Details Header */}
          <Card sx={{ mb: 3 }} className="print-header">
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="h5" fontWeight={600}>
                    {selectedEvent?.title}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Date:</strong> {selectedEvent && formatDate(selectedEvent.date)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Time:</strong> {selectedEvent && formatTime(selectedEvent.startTime)} - {selectedEvent && formatTime(selectedEvent.endTime)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Location:</strong> {selectedEvent?.location}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }} className="no-print">
                  <Button
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={handlePrint}
                    disabled={registrations.length === 0}
                  >
                    Print
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportCSV}
                    disabled={registrations.length === 0}
                  >
                    Export CSV
                  </Button>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {/* Summary Stats */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Card sx={{ minWidth: 150 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight={700} color="primary">
                  {confirmedCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Registrations
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 150 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight={700} color="success.main">
                  {totalAttendees}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Attendees
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 150 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight={700} color="error.main">
                  {cancelledCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cancelled
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 150 }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h4" fontWeight={700}>
                  {selectedEvent?.capacity}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Capacity
                </Typography>
              </CardContent>
            </Card>
          </Box>

          {/* Attendee Table */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : registrations.length === 0 ? (
            <Alert severity="info">
              No registrations for this event yet.
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>Name</strong></TableCell>
                    <TableCell><strong>Email</strong></TableCell>
                    <TableCell><strong>Phone</strong></TableCell>
                    <TableCell align="center"><strong>Attendees</strong></TableCell>
                    <TableCell><strong>Special Requirements</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                    <TableCell><strong>Registered</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {registrations.map((registration, index) => {
                    return (
                      <TableRow
                        key={registration.id}
                        sx={{
                          backgroundColor: registration.status === 'cancelled' ? '#ffebee' : 'inherit',
                          '&:hover': { backgroundColor: registration.status === 'cancelled' ? '#ffcdd2' : '#f5f5f5' },
                        }}
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Typography fontWeight={500}>
                            {registration.user?.firstName} {registration.user?.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell>{registration.user?.email || '-'}</TableCell>
                        <TableCell>{registration.user?.phone || '-'}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={registration.attendeeCount}
                            size="small"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          {registration.specialRequirements || '-'}
                        </TableCell>
                        <TableCell align="center">
                          {registration.status === 'confirmed' ? (
                            <Chip
                              icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                              label="Confirmed"
                              size="small"
                              color="success"
                            />
                          ) : registration.status === 'cancelled' ? (
                            <Chip
                              icon={<CancelIcon sx={{ fontSize: 16 }} />}
                              label="Cancelled"
                              size="small"
                              color="error"
                            />
                          ) : (
                            <Chip label={registration.status} size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          {new Date(registration.registeredAt).toLocaleDateString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* Print Styles */}
          <style>{`
            @media print {
              .no-print {
                display: none !important;
              }
              .print-header {
                margin-bottom: 20px !important;
              }
            }
          `}</style>
        </>
      )}
    </Box>
  );
};

export default EventRegisterPage;
